import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { BookOpen, FileCode2, Hash, Braces, RotateCw } from "lucide-react";
import { Topbar } from "@/components/Topbar";
import { Sidebar } from "@/components/Sidebar";
import { Preview } from "@/components/Preview";
import { Tutorial } from "@/components/Tutorial";

// Monaco is heavy (~1MB). Defer it until the user actually opens a code tab.
const CodeEditor = lazy(() =>
  import("@/components/CodeEditor").then((m) => ({ default: m.CodeEditor }))
);
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fetchCourses, fetchCourse } from "@/lib/api";
import { buildShareUrl, readShareFromUrl } from "@/lib/share";
import { downloadStandaloneHtml } from "@/lib/exportHtml";
import { toast } from "sonner";

const TABS = [
  { key: "tutorial", label: "教程", icon: BookOpen },
  { key: "html", label: "HTML", icon: FileCode2 },
  { key: "css", label: "CSS", icon: Hash },
  { key: "js", label: "JS", icon: Braces },
];

export default function Playground() {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [course, setCourse] = useState(null);
  const [code, setCode] = useState({ html: "", css: "", js: "" });
  const [activeTab, setActiveTab] = useState("tutorial");
  const [loading, setLoading] = useState(true);
  const [isWide, setIsWide] = useState(() => window.innerWidth >= 1024);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pendingShareRef = useRef(readShareFromUrl());

  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    fetchCourses()
      .then((list) => {
        setCourses(list);
        const ps = pendingShareRef.current;
        const shared = ps && list.find((c) => c.id === ps.id);
        if (shared) setActiveId(ps.id);
        else if (list.length) setActiveId(list[0].id);
      })
      .catch(() => toast.error("课程列表加载失败"))
      .finally(() => setCoursesLoading(false));
  }, []);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    fetchCourse(activeId)
      .then((c) => {
        setCourse(c);
        const ps = pendingShareRef.current;
        if (ps && ps.id === activeId && ps.code) {
          setCode({
            html: ps.code.html || "",
            css: ps.code.css || "",
            js: ps.code.js || "",
          });
          pendingShareRef.current = null;
          toast.success("已加载分享的代码");
        } else {
          setCode({
            html: c.code.html || "",
            css: c.code.css || "",
            js: c.code.js || "",
          });
        }
        setActiveTab("tutorial");
      })
      .catch(() => toast.error("课程内容加载失败"))
      .finally(() => setLoading(false));
  }, [activeId]);

  const onCodeChange = (tab, value) => {
    setCode((prev) => ({ ...prev, [tab]: value }));
  };

  const resetCode = () => {
    if (!course) return;
    setCode({
      html: course.code.html || "",
      css: course.code.css || "",
      js: course.code.js || "",
    });
    toast.success("已恢复初始代码");
  };

  const handleSelect = (id) => {
    setActiveId(id);
    setDrawerOpen(false);
  };

  const handleShare = async () => {
    if (!activeId) return;
    const url = buildShareUrl({ id: activeId, code });
    window.history.replaceState(null, "", url);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("分享链接已复制到剪贴板");
    } catch {
      toast.info("分享链接已生成在地址栏");
    }
  };

  const handleExport = () => {
    if (!course) return;
    downloadStandaloneHtml(course, code);
    toast.success("已导出为独立 HTML 文件");
  };

  const previewCode = useMemo(() => code, [code]);

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden">
      <Topbar
        onMenu={() => setDrawerOpen(true)}
        onShare={handleShare}
        onExport={handleExport}
        canShare={!!course}
      />

      {/* Mobile course drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="p-0 w-[280px] bg-[hsl(var(--surface))]" data-testid="course-drawer">
          <SheetHeader className="sr-only">
            <SheetTitle>课程目录</SheetTitle>
          </SheetHeader>
          <div className="h-full pt-2">
            <Sidebar courses={courses} activeId={activeId} onSelect={handleSelect} loading={coursesLoading} />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[260px_1fr] lg:grid-cols-[260px_minmax(0,1fr)_minmax(0,1fr)]">
        {/* Sidebar (desktop/tablet) */}
        <div className="hidden md:block border-r border-border min-h-0">
          <Sidebar courses={courses} activeId={activeId} onSelect={handleSelect} loading={coursesLoading} />
        </div>

        {/* Middle: tutorial + editor */}
        <div className="flex flex-col min-h-0 border-r border-border">
          <div className="flex items-stretch border-b border-border shrink-0 bg-[hsl(var(--surface))]">
            {TABS.map((t) => {
              const active = t.key === activeTab;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-mono border-b-2 transition-colors ${
                    active
                      ? "border-blue-500 text-foreground bg-background"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
            {activeTab !== "tutorial" && (
              <button
                data-testid="reset-code-btn"
                onClick={resetCode}
                title="恢复初始代码"
                className="ml-auto self-center mr-3 flex items-center gap-1 text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCw className="h-3 w-3" /> 重置
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0">
            {activeTab === "tutorial" ? (
              <Tutorial course={course} loading={loading} />
            ) : (
              <Suspense
                fallback={
                  <div className="p-4 text-xs font-mono text-muted-foreground">加载编辑器…</div>
                }
              >
                <CodeEditor code={code} activeTab={activeTab} onChange={onCodeChange} />
              </Suspense>
            )}
          </div>
        </div>

        {/* Right: preview + console (desktop) */}
        {isWide && (
          <div className="flex flex-col min-h-0">
            <Preview code={previewCode} />
          </div>
        )}
      </div>

      {/* Mobile/tablet preview below */}
      {!isWide && (
        <div className="h-[55vh] border-t border-border">
          <Preview code={previewCode} />
        </div>
      )}
    </div>
  );
}
